(() => {
  const POLICY_SELECTOR = '.shopify-policy__body .rte';
  const pageText = window.clpPageTranslations || {};
  const pageLabel = (key, fallback) => pageText[key] || fallback;

  const localeRoot = () => String(pageText.localeRoot || '/').replace(/\/$/, '');

  const localizePolicyLinks = (root) => {
    const prefix = localeRoot();
    if (!prefix) return;

    const shopHosts = new Set(['gallery.cerjlalonde.com', 'cerj-lalonde-prints.myshopify.com', window.location.hostname]);
    root.querySelectorAll('a[href]').forEach((link) => {
      const original = link.getAttribute('href');
      if (!original) return;
      let destination = original;

      if (/^(?:https?:)?\/\//i.test(original)) {
        try {
          const url = new URL(original, window.location.href);
          if (!shopHosts.has(url.hostname) || !/^https?:$/.test(url.protocol)) return;
          destination = url.pathname + url.search + url.hash;
        } catch (_error) {
          return;
        }
      }

      // Leave external, fragment, mail, and already localized destinations intact.
      if (!destination.startsWith('/') || destination.startsWith('//') || /^\/(?:fr|zh-cn)(?=\/|[?#]|$)/i.test(destination)) return;
      const localized = prefix + destination;
      if (localized !== original) link.setAttribute('href', localized);
    });
  };

  const normalizeText = (value) => value.replace(/\s+/g, ' ').trim();

  const slugify = (value) =>
    normalizeText(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'section';

  const isStrongOnlyParagraph = (node) => {
    if (!(node instanceof HTMLElement) || node.tagName !== 'P') return false;
    if (node.children.length !== 1 || node.firstElementChild?.tagName !== 'STRONG') return false;

    return normalizeText(node.textContent || '') === normalizeText(node.firstElementChild.textContent || '');
  };

  const policyPath = () => {
    const canonical = document.querySelector('link[rel="canonical"]')?.href;
    try {
      return new URL(canonical || window.location.href).pathname;
    } catch (_error) {
      return window.location.pathname;
    }
  };

  const sourceFor = (root, path) => {
    if (
      path.endsWith('/policies/privacy-policy') &&
      root.children.length === 1 &&
      root.firstElementChild?.tagName === 'DIV'
    ) {
      return root.firstElementChild;
    }

    return root;
  };

  const isHeading = (node, path, index) => {
    if (!(node instanceof HTMLElement)) return false;

    if (path.endsWith('/policies/privacy-policy')) return node.tagName === 'H2';
    if (path.endsWith('/policies/shipping-policy')) return isStrongOnlyParagraph(node);
    if (path.endsWith('/policies/refund-policy')) {
      const text = normalizeText(node.textContent || '');
      return isStrongOnlyParagraph(node) && (index === 0 || /^\d+\.\s+/.test(text));
    }
    if (path.endsWith('/policies/contact-information')) return index === 0 && isStrongOnlyParagraph(node);

    if (path.endsWith('/policies/terms-of-service')) {
      return isStrongOnlyParagraph(node) && /^\d+\.\s+/.test(normalizeText(node.textContent || ''));
    }

    return false;
  };

  const headingData = (node, fallbackNumber) => {
    const raw = normalizeText(node.textContent || '');
    const numbered = raw.match(/^(\d+)\.\s+(.+)$/);

    return {
      number: numbered?.[1] || String(fallbackNumber),
      title: numbered?.[2] || raw,
    };
  };

  const uniqueId = (title, usedIds) => {
    const base = `policy-${slugify(title)}`;
    let candidate = base;
    let suffix = 2;

    while (usedIds.has(candidate) || document.getElementById(candidate)) {
      candidate = `${base}-${suffix}`;
      suffix += 1;
    }

    usedIds.add(candidate);
    return candidate;
  };

  const enhancePolicy = () => {
    const root = document.querySelector(`${POLICY_SELECTOR}:not([data-clp-policy-layout])`);
    if (!root) return;

    const path = policyPath();
    if (!path.includes('/policies/')) return;

    localizePolicyLinks(root);

    const source = sourceFor(root, path);
    let nodes = Array.from(source.children);

    if (path.endsWith('/policies/refund-policy')) {
      nodes = nodes.flatMap((node) => {
        const text = normalizeText(node.textContent || '');
        let title = '';

        if (text === pageLabel('policyMuralSentence', 'Murals are covered by the same guarantee.')) title = pageLabel('policyMurals', '2. MURALS');
        if (text.startsWith(pageLabel('policyRefundPrefix', 'An approved refund will be issued'))) title = pageLabel('policyRefunds', '3. REFUNDS');
        if (!title) return [node];

        const heading = document.createElement('p');
        const strong = document.createElement('strong');
        strong.textContent = title;
        heading.append(strong);
        return [heading, node];
      });
    }
    const headingIndexes = nodes
      .map((node, index) => (isHeading(node, path, index) ? index : -1))
      .filter((index) => index >= 0);

    if (!headingIndexes.length) return;

    root.dataset.clpPolicyLayout = 'ready';

    const layout = document.createElement('div');
    layout.className = 'clp-policy-layout';

    const split = document.createElement('div');
    split.className = 'clp-policy__split';

    const column = document.createElement('div');
    column.className = 'clp-policy__col';

    const rail = document.createElement('nav');
    rail.className = 'clp-policy__rail';
    rail.setAttribute('aria-label', pageLabel('policyOnThisPage', 'On this page'));

    const railList = document.createElement('ol');
    railList.className = 'clp-policy__rail-list';

    const firstHeadingIndex = headingIndexes[0];
    if (firstHeadingIndex > 0) {
      const preamble = document.createElement('div');
      preamble.className = 'clp-policy__preamble';
      nodes.slice(0, firstHeadingIndex).forEach((node) => preamble.append(node));
      column.append(preamble);
    }

    const usedIds = new Set();

    headingIndexes.forEach((headingIndex, sectionIndex) => {
      const headingNode = nodes[headingIndex];
      const nextHeadingIndex = headingIndexes[sectionIndex + 1] ?? nodes.length;
      const { number, title } = headingData(headingNode, sectionIndex + 1);
      const id = uniqueId(title, usedIds);

      const article = document.createElement('article');
      article.className = 'clp-policy__clause';
      article.id = id;

      const heading = document.createElement('h2');
      heading.className = 'clp-policy__clause-head';

      const numberElement = document.createElement('span');
      numberElement.className = 'clp-policy__num';
      numberElement.setAttribute('aria-hidden', 'true');
      numberElement.textContent = number;

      const titleElement = document.createElement('span');
      titleElement.className = 'clp-policy__clause-title';
      titleElement.textContent = title;

      heading.append(numberElement, titleElement);

      const body = document.createElement('div');
      body.className = 'clp-policy__clause-body';
      nodes.slice(headingIndex + 1, nextHeadingIndex).forEach((node) => body.append(node));

      article.append(heading, body);
      column.append(article);

      const item = document.createElement('li');
      item.className = 'clp-policy__rail-item';

      const link = document.createElement('a');
      link.className = 'clp-policy__rail-link';
      link.href = `#${id}`;

      const railNumber = document.createElement('span');
      railNumber.className = 'clp-policy__rail-num';
      railNumber.setAttribute('aria-hidden', 'true');
      railNumber.textContent = number;

      const railTitle = document.createElement('span');
      railTitle.textContent = title;

      link.append(railNumber, railTitle);
      item.append(link);
      railList.append(item);
    });

    rail.append(railList);
    split.append(column, rail);

    const ctaWrap = document.createElement('div');
    ctaWrap.className = 'clp-policy__cta-wrap';

    const cta = document.createElement('a');
    cta.className = 'clp-policy__cta';
    cta.href = `${localeRoot()}/collections/all`;
    cta.textContent = pageLabel('policyExploreCollections', 'Explore the Collections');

    ctaWrap.append(cta);
    layout.append(split, ctaWrap);
    root.replaceChildren(layout);
  };

  enhancePolicy();
  document.addEventListener('DOMContentLoaded', enhancePolicy, { once: true });
  document.addEventListener('pageshow', enhancePolicy);
  document.addEventListener('shopify:section:load', enhancePolicy);

  new MutationObserver(enhancePolicy).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
})();
